import { useEffect, useState } from 'react'

/**
 * create a hook with shared state. all instances of the created hook will have a synchronized state.
 * the state will remain even if all instances of the hook are unmounted. Warning: the internal state is not reset between
 * executions of it-blocks in cypress/jest tests. Make sure to clean up the internal state at the end of the test.
 *
 * @param {any} initialState - inital state of the hook
 * @returns {() => [value, setValue]} - a setState hook that has it's state synchronized across all instances
 */
export const createSharedStateHook = initialState => {
    let currentState = initialState
    let currentIndex = 0
    const listeners = {}
    const addListener = listenerFn => {
        const id = currentIndex
        currentIndex++
        listeners[id] = listenerFn
        return () => {
            delete listeners[id]
        }
    }

    const onUpdate = update => {
        const newState = typeof update === 'function' ? update(currentState) : update
        currentState = newState
        Object.values(listeners).forEach(fn => typeof fn === 'function' && fn(newState))
    }

    return function useSharedState() {
        const [state, setState] = useState(currentState)

        useEffect(() => addListener(setState), [setState])

        return [state, onUpdate]
    }
}
